import React, { useEffect, useRef, useState } from 'react';
import OwnerRoundTripViewer from './OwnerRoundTripViewer';
import CameraAudienceChooser from './CameraAudienceChooser';

import './Camera.css';

export const ANDROID_CAMERA_SETTINGS_STEPS = 'Settings → Apps → Kata.fit → Permissions → Camera → Allow only while using the app';

function PermissionNotice({ camera }) {
  const outcome = camera.permission?.outcome;
  if (!outcome) return null;
  if (outcome === 'checking') return <p className="camera-panel-note" role="status">Checking camera permission…</p>;
  if (outcome === 'allowed-after-settings') {
    return <p className="camera-panel-note" role="status">Camera permission is allowed. Tap Start or Preview to continue.</p>;
  }
  const lead = outcome === 'denied' ? 'Camera permission was not allowed.'
    : outcome === 'blocked' ? 'Android is no longer showing the camera permission prompt for Kata.fit.'
      : 'Kata.fit could not ask Android for camera permission.';
  return (
    <div className="camera-permission-help" role="alert">
      <p><strong>{lead}</strong> To use the camera, allow it in Android settings:</p>
      <p className="camera-permission-steps">{ANDROID_CAMERA_SETTINGS_STEPS}</p>
      <p className="camera-panel-note">Wording can vary by Android version. On Android 12 or newer, also make sure Camera access is turned on in Settings → Security &amp; privacy → Privacy controls (or the quick settings Camera access tile).</p>
      <p className="camera-panel-note">When you come back, tap Start or Preview again. Kata.fit never starts the camera on its own.</p>
      <button type="button" className="camera-secondary-btn" onClick={() => { void camera.openAppSettings(); }}>Open app settings</button>
    </div>
  );
}

function PreviewVideo({ stream }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream || null;
  }, [stream]);
  return <video ref={ref} className="camera-preview-video" autoPlay muted playsInline aria-label="This device's camera feed" />;
}

// Minimal anchored floating camera window (not a modal): Off shows Start; active
// shows this device's own feed and Stop. Close hides the window only.
export default function CameraPanel({ camera, onOpenVideos }) {
  const { state } = camera;
  const tracer = state.settings?.privateTracer || (state.session?.privateTracer ? { enabled: true, allowed: true, videoOnly: true } : null);
  const saved = tracer ? [] : state.settings?.default_audiences;
  const [draft, setDraft] = useState(null);
  // A controller is one login/owner generation. Never render a previous
  // generation's intent while waiting for an effect to reset local state.
  const audiences = tracer ? [] : draft?.owner === camera.ownerGeneration ? draft.audiences
    : Array.isArray(saved) ? saved : null;
  useEffect(() => { if (!camera.panelOpen) setDraft(null); }, [camera.panelOpen]);
  if (!camera.panelOpen) return null;

  const status = state.status;
  const capturing = ['requesting_camera', 'preview', 'creating', 'connecting', 'live'].includes(status);
  const checking = camera.permission?.outcome === 'checking';
  const stopping = camera.stopping && !capturing;
  // Start in progress before capture (settings, session verification, permission).
  const preparing = camera.startPending && !capturing;
  const error = stopping || preparing ? null : camera.actionError || state.error;
  const idle = !capturing && !checking && !stopping && !preparing && status !== 'stop_unconfirmed';
  const live = state.session?.live;

  let body;
  if (!camera.ready) {
    // Fail closed with the actual reason; no state here offers capture.
    body = camera.availability === 'unconfigured'
      ? <p className="camera-panel-note" role="status">Camera is unavailable right now: the camera service isn&apos;t set up for Kata.fit yet. Nothing is recorded.</p>
      : camera.availability === 'failed'
        ? <p className="camera-panel-error" role="alert">Camera couldn&apos;t be set up. Nothing is recorded. Fully quit and reopen Kata.fit to try again.</p>
        : <p className="camera-panel-note" role="status">Your Kata.fit account is still connecting. Camera actions become available once it finishes.</p>;
  } else {
    body = (
      <>
        {(status === 'preview' || status === 'live' || status === 'creating' || status === 'connecting') && state.previewStream && (
          <PreviewVideo stream={state.previewStream} />
        )}
        {status === 'requesting_camera' && <p className="camera-panel-note" role="status">Waiting for the camera…</p>}
        {(status === 'creating' || status === 'connecting' || (preparing && !checking)) && <p className="camera-panel-note" role="status">Starting…</p>}
        {stopping && <p className="camera-panel-note" role="status">Stopping…</p>}
        {status === 'live' && tracer && camera.returnViewer && <OwnerRoundTripViewer viewer={camera.returnViewer} sessionId={state.session.id} />}
        {status === 'live' && <p className="camera-live-badge" role="status">● Live{live ? ` · Live ${live.day} · ${live.deviceName}` : ''}</p>}
        {status === 'preview' && (
          <>
            {!tracer && <CameraAudienceChooser value={audiences} onChange={value => { setDraft({ owner: camera.ownerGeneration, audiences: value }); }} />}
            {!Array.isArray(audiences) && <p className="camera-panel-note">Choose who can watch before starting.</p>}
          </>
        )}
        <PermissionNotice camera={camera} />
        {error && <p className="camera-panel-error" role="alert">{error}</p>}
        <div className="camera-panel-actions">
          {idle && <button type="button" className="camera-primary-btn" disabled={tracer && !tracer.allowed} onClick={() => { void camera.startLive(); }}>Start</button>}
          {status === 'preview' && (
            <button type="button" className="camera-primary-btn" disabled={!Array.isArray(audiences)}
              onClick={() => { void camera.start(audiences); }}>Start</button>
          )}
          {(checking || preparing || status === 'requesting_camera' || status === 'preview') && (
            <button type="button" className="camera-secondary-btn" onClick={camera.cancel}>Cancel</button>
          )}
          {['creating', 'connecting', 'live'].includes(status) && (
            <button type="button" className="camera-danger-btn" onClick={() => { void camera.stop(); }}>Stop</button>
          )}
          {status === 'stop_unconfirmed' && !stopping && (
            <button type="button" className="camera-danger-btn" onClick={() => { void camera.retryStop(); }}>Retry stop</button>
          )}
        </div>
        {idle && (
          <div className="camera-float-options">
            <p className="camera-panel-note">{tracer ? 'Private · Video only' : 'Private · only you. Saved sharing applies to Preview only.'}</p>
            {camera.cameras && (
              <select aria-label="Camera source" value={camera.cameraId} onChange={e => camera.setCameraId(e.target.value)}>
                <option value="">Default camera</option>
                {camera.cameras.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            )}
            {!tracer && (
              <label className="camera-mic-toggle">
                <input type="checkbox" aria-label="Include microphone audio" checked={camera.audioOptIn}
                  disabled={camera.android} onChange={e => camera.setAudioOptIn(e.target.checked)} />
                <span>{camera.android ? 'Microphone is not available in the Android app yet.' : 'Microphone (off unless you turn it on)'}</span>
              </label>
            )}
            <button type="button" className="camera-link-btn" disabled={tracer && !tracer.allowed} onClick={() => { void camera.preview(); }}>Preview</button>
            {/* Secondary only while Off: the live feed surface carries no library navigation. */}
            {onOpenVideos && <button type="button" className="camera-link-btn" onClick={() => { camera.closePanel(); onOpenVideos(); }}>Videos in Media</button>}
          </div>
        )}
      </>
    );
  }

  return (
    <section className="camera-float" data-testid="camera-panel" role="dialog" aria-modal="false" aria-label="Camera">
      <header className="camera-panel-header">
        <h2>Camera</h2>
        <button type="button" className="camera-close-btn" onClick={camera.closePanel}>Close</button>
      </header>
      {camera.ready && idle && <p className="camera-panel-intro">Start records this device into today&apos;s Live Media. Nothing is captured until you tap Start. Close keeps it live; Stop ends it.</p>}
      {body}
    </section>
  );
}
